import { File as FileIcon, Upload, X } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';

import { ProgressBar } from '@/components/ui/progress-bar';
import { cn } from '@/utils/cn';

/**
 * Drag-and-drop or click to upload.
 *
 * Files go straight to object storage through a signed URL — they never pass through
 * the API. A church uploading a two-hour sermon recording should not be limited by an
 * Express request body, and the API should not be holding a connection open for
 * twenty minutes.
 *
 * The hidden `<input type="file">` inside is the one place a native file input is
 * allowed, because it is the accessible way to open the picker.
 */

export interface FileDropzoneProps {
  onFiles: (files: File[]) => void;
  /** Mime types or extensions, as the `accept` attribute takes them. */
  accept?: string;
  /** Human-readable list of what is accepted, already translated. */
  acceptLabel?: string;
  maxBytes?: number;
  maxSizeLabel?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** 0–100 while uploading. */
  progress?: number;
  progressLabel?: string;
  /** Set when a chosen file was refused; already translated. */
  error?: string;
  labels: {
    prompt: string;
    remove: string;
  };
  /** Files already chosen, for the list under the zone. */
  files?: { name: string; size?: string }[];
  onRemove?: (index: number) => void;
  className?: string;
}

export const FileDropzone = ({
  onFiles,
  accept,
  acceptLabel,
  maxBytes,
  maxSizeLabel,
  multiple = false,
  disabled = false,
  progress,
  progressLabel,
  error,
  labels,
  files,
  onRemove,
  className,
}: FileDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const accepted = (candidates: FileList | null) => {
    if (!candidates) return;
    const list = Array.from(candidates).filter(
      (file) => maxBytes === undefined || file.size <= maxBytes,
    );
    if (list.length > 0) onFiles(multiple ? list : list.slice(0, 1));
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) accepted(event.dataTransfer.files);
  };

  return (
    <div className={className}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        aria-describedby={error ? 'dropzone-error' : undefined}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors',
          dragging ? 'border-brand bg-brand/5' : 'border-border hover:border-border-strong',
          error && 'border-danger',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <Upload aria-hidden className="h-6 w-6 text-fg-tertiary" />
        <span className="text-body-sm text-fg">{labels.prompt}</span>
        {acceptLabel || maxSizeLabel ? (
          <span className="text-caption text-fg-tertiary">
            {[acceptLabel, maxSizeLabel].filter(Boolean).join(' · ')}
          </span>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(event) => {
            accepted(event.target.files);
            // Reset so choosing the same file twice in a row still fires a change.
            event.target.value = '';
          }}
          className="sr-only"
        />
      </button>

      {error ? (
        <p id="dropzone-error" role="alert" className="mt-1.5 text-caption text-danger-fg">
          {error}
        </p>
      ) : null}

      {progress !== undefined ? (
        <ProgressBar
          value={progress}
          label={progressLabel ?? labels.prompt}
          className="mt-3"
          size="sm"
        />
      ) : null}

      {files && files.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-2"
            >
              <FileIcon aria-hidden className="h-4 w-4 shrink-0 text-fg-tertiary" />
              <span className="min-w-0 flex-1 truncate text-body-sm text-fg">{file.name}</span>
              {file.size ? (
                <span className="shrink-0 text-caption text-fg-tertiary">{file.size}</span>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  aria-label={labels.remove}
                  onClick={() => onRemove(index)}
                  className="shrink-0 rounded p-0.5 text-fg-tertiary hover:text-fg"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
